import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import {
  Package, Shield, ShoppingCart, Globe, Code, Brain, Smartphone, Server,
  Loader2, Check, Upload, ClipboardCheck, Lightbulb, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { StatBar } from "@/components/shared/StatBar";
import { EmptyState } from "@/components/shared/EmptyState";

const PACK_ICONS = {
  "saas-starter": Package,
  "mobile-app": Smartphone,
  "api-backend": Server,
  "marketing-website": Globe,
  "webapp-essentials": Code,
  "ecommerce": ShoppingCart,
  "auth-security": Shield,
  "ai-integration": Brain,
};

export default function PacksPage() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPack, setBusyPack] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadPacks(); }, []);

  const loadPacks = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/library/packs");
      setPacks(data);
    } catch (e) {
      toast.error("Failed to load packs");
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async (packId) => {
    setBusyPack(packId);
    try {
      const { data } = await API.post(`/library/packs/${packId}/install`);
      toast.success(`Installed "${data.pack}"`, { description: `${data.installed} blocks added to your library` });
      loadPacks();
    } catch (e) {
      toast.error("Install failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setBusyPack(null);
    }
  };

  const handleUpdate = async (packId) => {
    setBusyPack(packId);
    try {
      const { data } = await API.put(`/library/packs/${packId}/update`);
      toast.success(`Updated "${data.pack}"`, { description: `${data.removed} old blocks removed, ${data.updated} new blocks installed` });
      loadPacks();
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setBusyPack(null);
    }
  };

  const totalBlocks = packs.reduce((sum, p) => sum + (p.block_count || 0), 0);
  const installedCount = packs.filter((p) => p.installed).length;

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]" data-testid="packs-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Page header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[var(--pf-text)]" data-testid="packs-title">Packs</h1>
            <p className="text-xs text-[var(--pf-text-secondary)] mt-1">Supply side of your library — install curated packs, import your own prompts, or audit what you have.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/library?action=audit")} data-testid="packs-action-audit"
              variant="outline" className="rounded-none border-[var(--pf-border)] h-9 px-4 text-xs hover:border-[var(--pf-accent)]">
              <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />Audit Library
            </Button>
            <Button onClick={() => navigate("/library?action=suggest")} data-testid="packs-action-suggest"
              variant="outline" className="rounded-none border-[var(--pf-border)] h-9 px-4 text-xs hover:border-[var(--pf-accent)]">
              <Lightbulb className="w-3.5 h-3.5 mr-1.5" />Suggest Additions
            </Button>
            <Button onClick={() => navigate("/library?action=import")} data-testid="packs-action-import"
              className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-9 px-4 text-xs font-medium">
              <Upload className="w-3.5 h-3.5 mr-1.5" />Import Prompts
            </Button>
          </div>
        </div>

        {/* Stats bar */}
        {!loading && (
          <StatBar
            testId="packs-summary"
            stats={[
              { label: "packs available", value: packs.length },
              { label: "installed", value: installedCount },
              { label: "total blocks", value: totalBlocks },
            ]}
          />
        )}

        {/* Packs grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--pf-accent)]" />
          </div>
        ) : packs.length === 0 ? (
          <EmptyState icon={Package} title="No packs available" testId="packs-empty" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="packs-grid">
            {packs.map((pack) => {
              const Icon = PACK_ICONS[pack.id] || Package;
              const isBusy = busyPack === pack.id;
              return (
                <div key={pack.id}
                  className={`border ${pack.installed ? "border-[#16A34A]/50" : "border-[var(--pf-border)]"} bg-[var(--pf-bg)] p-5 flex flex-col hover:border-[var(--pf-accent)] transition-colors`}
                  data-testid={`pack-card-${pack.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-[var(--pf-surface)] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[var(--pf-text-secondary)]" />
                    </div>
                    {pack.installed && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium flex items-center gap-1" data-testid={`pack-installed-${pack.id}`}>
                        <Check className="w-3 h-3" />Installed
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-sm font-bold text-[var(--pf-text)] mb-1">{pack.name}</h3>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)] border border-[var(--pf-border)] uppercase tracking-wider">{pack.build_type}</span>
                    <span className="text-[10px] text-[var(--pf-text-muted)]">{pack.block_count} blocks</span>
                  </div>
                  <p className="text-xs text-[var(--pf-text-secondary)] leading-relaxed flex-1 mb-4">{pack.description}</p>
                  {pack.installed ? (
                    <Button onClick={() => handleUpdate(pack.id)} disabled={isBusy} variant="outline" data-testid={`update-pack-${pack.id}`}
                      className="w-full rounded-none border-[var(--pf-border)] h-8 text-xs hover:border-[var(--pf-accent)]">
                      {isBusy ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Updating...</> : <><RefreshCw className="w-3 h-3 mr-1.5" />Update Pack</>}
                    </Button>
                  ) : (
                    <Button onClick={() => handleInstall(pack.id)} disabled={isBusy} data-testid={`install-pack-${pack.id}`}
                      className="w-full rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-8 text-xs font-medium">
                      {isBusy ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Installing...</> : <>Install Pack</>}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
